import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, CircularProgress, Chip } from '@mui/material'
import {
  Zap,
  Calendar,
  TrendingUp,
  Flame,
  MessageCircle,
  Users,
  Mic,
  Radio,
  ArrowRight,
  Star,
  BarChart3,
} from 'lucide-react'
import {
  useGetEngagementStatsQuery,
  useGetAudienceInsightsQuery,
  useGetEpisodePerformanceQuery,
  useGetListeningTopicsQuery,
  useCreateListeningTopicMutation,
  Platform,
} from 'graphql/generated/graphql'
import { usePageNavigation } from 'hooks/useNavigation'
import { LOCAL, DEVELOP } from 'constants/env'
import InfoTooltip from 'components/InfoTooltip/InfoTooltip'

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

const platformEmoji: Record<string, string> = {
  Instagram: '📷', TikTok: '🎵', Youtube: '🎥', Reddit: '🔴', Twitter: '🐦', Bluesky: '🦋',
}

export default function Dashboard(): JSX.Element {
  const { navigateToListeningTopics, navigateToAnalytics } = usePageNavigation()
  const { data: engagementData, loading: engLoading } = useGetEngagementStatsQuery({ pollInterval: 60000 })
  const { data: insightsData, loading: insightsLoading } = useGetAudienceInsightsQuery()
  const { data: episodeData, loading: epLoading } = useGetEpisodePerformanceQuery()
  const { data: topicsData, loading: topicsLoading } = useGetListeningTopicsQuery()
  const [podcastStats, setPodcastStats] = useState<any>(null)
  const [statsLoading, setStatsLoading] = useState(true)
  const [createTopic] = useCreateListeningTopicMutation()
  const [createdSignals, setCreatedSignals] = useState<Set<string>>(new Set())

  const handleCreateCrossPromo = (podTitle: string): void => {
    createTopic({
      variables: {
        input: {
          name: `Cross: ${podTitle}`.slice(0, 50),
          description: `Conversations about "${podTitle}" podcast and its audience. Find their listeners and engage with them.`,
          platforms: [Platform.Instagram, Platform.TikTok],
        },
      },
    }).then(() => {
      setCreatedSignals((prev) => new Set(prev).add(podTitle))
    }).catch(console.error)
  }

  const stats = engagementData?.getEngagementStats
  const insights = insightsData?.getAudienceInsights
  const topics = topicsData?.getListeningTopics ?? []
  const episodes = (episodeData?.getEpisodePerformance ?? []).filter(
    (ep) => ep.topicName.startsWith('EP:') || ep.topicName.startsWith('Latest:')
  )

  useEffect(() => {
    fetch(`${config.API_URL}/api/podcast/stats`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json()
        if (data.success) setPodcastStats(data.data)
      })
      .catch(console.error)
      .finally(() => setStatsLoading(false))
  }, [])

  const isLoading = engLoading || insightsLoading || topicsLoading

  const hasData = (stats?.total ?? 0) > 0 || topics.length > 0

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '24px', md: '34px' }, mb: 0.5 }}>
          {podcastStats?.podcast?.title ? `${String(podcastStats.podcast.title)}` : 'Dashboard'}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px' }}>
          Your podcast growth at a glance
        </Typography>
      </Box>

      {/* Loading skeleton */}
      {isLoading && (
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' }}>
                <Box sx={{ width: 60, height: 8, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1, mb: 1, '@keyframes pulse': { '0%, 100%': { opacity: 0.4 }, '50%': { opacity: 0.8 } }, animation: 'pulse 1.5s ease-in-out infinite' }} />
                <Box sx={{ width: 40, height: 24, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1, '@keyframes pulse': { '0%, 100%': { opacity: 0.4 }, '50%': { opacity: 0.8 } }, animation: 'pulse 1.5s ease-in-out infinite' }} />
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {[1, 2].map((i) => (
              <Box key={i} sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', height: 140, '@keyframes pulse': { '0%, 100%': { opacity: 0.4 }, '50%': { opacity: 0.8 } }, animation: 'pulse 1.5s ease-in-out infinite' }} />
            ))}
          </Box>
        </Box>
      )}

      {/* Empty state for new users */}
      {!hasData && (
        <Box sx={{ textAlign: 'center', py: 8, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '20px', border: '1px dashed rgba(255,255,255,0.1)' }}>
          <Radio size={48} color="rgba(168, 85, 247, 0.5)" />
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', mt: 2, fontSize: '18px', fontWeight: 500 }}>
            Welcome to Nalapod
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', mt: 1, mb: 3, maxWidth: 400, mx: 'auto' }}>
            Connect your podcast to start finding conversations and growing your audience.
          </Typography>
          <Button
            onClick={navigateToListeningTopics}
            endIcon={<ArrowRight size={16} />}
            sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', color: 'white', borderRadius: '12px', textTransform: 'none', px: 4, py: 1.2, fontWeight: 600 }}
          >
            Set Up Social Radar
          </Button>
        </Box>
      )}

      {hasData && (
        <>
          {/* Engagement stats row */}
          {stats && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
              <StatCard icon={<Zap size={16} />} label="Today" value={stats.today} />
              <StatCard icon={<Calendar size={16} />} label="This Week" value={stats.thisWeek} />
              <StatCard icon={<TrendingUp size={16} />} label="This Month" value={stats.thisMonth} />
              <StatCard icon={<MessageCircle size={16} />} label="Total Engagements" value={stats.total} />
              {stats.streakDays > 1 && <StatCard icon={<Flame size={16} />} label="Day Streak" value={stats.streakDays} highlight />}
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            {/* Podcast overview */}
            {podcastStats?.podcast && (
              <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {podcastStats.podcast.imageUrl && (
                    <Box component="img" src={podcastStats.podcast.imageUrl} sx={{ width: 52, height: 52, borderRadius: '12px', objectFit: 'cover' }} />
                  )}
                  <Box>
                    <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '16px' }}>{podcastStats.podcast.title}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {podcastStats.podcast.ratingAverage != null && (
                        <>
                          <Star size={14} color="#f59e0b" fill="#f59e0b" />
                          <Typography sx={{ color: '#f59e0b', fontWeight: 700, fontSize: '14px' }}>{String(podcastStats.podcast.ratingAverage.toFixed(1))}</Typography>
                          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>({String(podcastStats.podcast.ratingCount)})</Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>{String(podcastStats.podcast.numberOfEpisodes)} episodes</Typography>
                  {podcastStats.podcast.daysBetweenEpisodes && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>Every {String(podcastStats.podcast.daysBetweenEpisodes)} days</Typography>
                  )}
                  {podcastStats.podcast.reviewCount > 0 && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>{String(podcastStats.podcast.reviewCount)} reviews</Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Active signals */}
            <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Radio size={16} color="#a855f7" />
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Active Signals<InfoTooltip text="Signals scan social media daily for conversations matching your podcast topics. Each signal represents a topic, episode, or mention pattern you're tracking." /></Typography>
                </Box>
                <Button onClick={navigateToListeningTopics} size="small" sx={{ color: '#a855f7', textTransform: 'none', fontSize: '12px' }}>View all</Button>
              </Box>
              {topics.length === 0 ? (
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>No signals yet</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {topics.slice(0, 4).map((topic) => (
                    <Box key={topic.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: 'white', fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, mr: 1 }}>{topic.name}</Typography>
                      <Typography sx={{ color: '#a855f7', fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>{String(topic.contentCount)} posts</Typography>
                    </Box>
                  ))}
                  {topics.length > 4 && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px' }}>+{String(topics.length - 4)} more</Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            {/* Audience breakdown */}
            {insights && insights.platformBreakdown.length > 0 && (
              <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Where Your Audience Is<InfoTooltip text="Breakdown of which social platforms the creators in your niche are most active on. Helps you focus your engagement on the right platforms." /></Typography>
                  <Button onClick={navigateToAnalytics} size="small" sx={{ color: '#a855f7', textTransform: 'none', fontSize: '12px' }}>Details</Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {insights.platformBreakdown.slice(0, 4).map((p) => (
                    <Box key={p.platform} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography sx={{ fontSize: '14px', width: 22 }}>{platformEmoji[p.platform] ?? '🌐'}</Typography>
                      <Typography sx={{ color: 'white', fontSize: '13px', width: 70 }}>{p.platform}</Typography>
                      <Box sx={{ flex: 1, height: 6, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: `${String(p.percentage)}%`, height: '100%', background: 'linear-gradient(135deg, #a855f7, #6366f1)', borderRadius: 3 }} />
                      </Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', width: 30, textAlign: 'right' }}>{String(p.percentage)}%</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Engagement trend */}
            {insights && insights.engagementTrend.length > 0 && (
              <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingUp size={16} color="#a855f7" />
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>14-Day Trend<InfoTooltip text="Your daily engagement activity over the last 2 weeks. Consistent daily engagement leads to the best audience growth results." /></Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 60 }}>
                  {insights.engagementTrend.map((day) => {
                    const maxCount = Math.max(...insights.engagementTrend.map((d) => d.count), 1)
                    const height = (day.count / maxCount) * 100
                    return (
                      <Box key={day.date} sx={{
                        flex: 1, height: `${String(Math.max(height, 4))}%`,
                        background: day.count > 0 ? 'linear-gradient(180deg, #a855f7, #6366f1)' : 'rgba(255,255,255,0.05)',
                        borderRadius: '2px 2px 0 0', minWidth: 6,
                      }} title={`${day.date}: ${String(day.count)}`} />
                    )
                  })}
                </Box>
              </Box>
            )}
          </Box>

          {/* Episode performance */}
          {episodes.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Mic size={16} color="#a855f7" />
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Recent Episodes<InfoTooltip text="How each episode signal is performing. 'Found' = social posts matching the episode topic. 'Engaged' = posts you've replied to." /></Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
                {episodes.slice(0, 5).map((ep) => (
                  <Box key={ep.topicId} sx={{ minWidth: 200, p: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', flexShrink: 0 }}>
                    <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '13px', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ep.topicName.replace(/^(EP|Latest): ?/, '')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{String(ep.contentCount)} found</Typography>
                      <Typography sx={{ fontSize: '11px', color: '#a855f7', fontWeight: 600 }}>{String(ep.engagementCount)} engaged</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Quick actions */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button onClick={navigateToListeningTopics} startIcon={<Zap size={16} />} sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', color: 'white', borderRadius: '12px', textTransform: 'none', px: 3, py: 1, fontWeight: 600 }}>
              Open Engagement Queue
            </Button>
            <Button onClick={navigateToAnalytics} startIcon={<BarChart3 size={16} />} sx={{ color: 'white', border: '1px solid rgba(168,85,247,0.3)', borderRadius: '12px', textTransform: 'none', px: 3, py: 1, fontWeight: 600, '&:hover': { borderColor: '#a855f7' } }}>
              View Analytics
            </Button>
          </Box>

          {/* Similar podcasts */}
          {podcastStats?.similarPodcasts?.length > 0 && (
            <Box sx={{ mt: 3, p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', mb: 2 }}>Cross-Promo Opportunities<InfoTooltip text="Podcasts similar to yours from Podchaser. Click 'Engage Audience' to create a signal that finds their listeners on social media — great for cross-promotion partnerships." /></Typography>
              <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
                {podcastStats.similarPodcasts.map((pod: any) => (
                  <Box key={pod.id} sx={{ minWidth: 160, p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', flexShrink: 0, textAlign: 'center' }}>
                    {pod.imageUrl && <Box component="img" src={pod.imageUrl} sx={{ width: 44, height: 44, borderRadius: '10px', objectFit: 'cover', mx: 'auto', mb: 0.5 }} />}
                    <Typography sx={{ color: 'white', fontSize: '11px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}>{pod.title}</Typography>
                    {pod.ratingAverage && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', mb: 0.75 }}>
                        <Star size={10} color="#f59e0b" fill="#f59e0b" />
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>{String(pod.ratingAverage.toFixed(1))}</Typography>
                      </Box>
                    )}
                    <Button
                      onClick={() => handleCreateCrossPromo(String(pod.title))}
                      disabled={createdSignals.has(String(pod.title))}
                      size="small"
                      fullWidth
                      sx={{
                        background: createdSignals.has(String(pod.title)) ? 'rgba(34,197,94,0.1)' : 'rgba(168,85,247,0.1)',
                        color: createdSignals.has(String(pod.title)) ? '#22c55e' : '#a855f7',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontSize: '10px',
                        py: 0.25,
                      }}
                    >
                      {createdSignals.has(String(pod.title)) ? '✓ Signal Created' : 'Engage Audience'}
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

function StatCard({ icon, label, value, highlight }: { icon: React.ReactNode; label: string; value: number; highlight?: boolean }): JSX.Element {
  return (
    <Box sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: highlight ? '#f59e0b' : '#a855f7' }}>{icon}</Box>
      <Typography sx={{ color: highlight ? '#f59e0b' : 'white', fontWeight: 800, fontSize: '24px', lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', mt: 0.25 }}>{label}</Typography>
    </Box>
  )
}
